import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Tag,
  Collapse,
  Descriptions,
  Empty,
  Spin,
  Alert,
  Row,
  Col,
  Statistic,
  Modal,
  Typography,
  Popover,
  Divider,
  Button,
  InputNumber,
  Space,
  message,
  Input,
  Popconfirm,
  Table,
  Form,
  Select,
} from 'antd';
import { PlusOutlined, DeleteOutlined, BookOutlined, CopyOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { adminPageWrap, adminAlertStyle, adminContentCardStyle } from '../styles/adminPageShell';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { useTalentPoolLabels } from '../contexts/TalentPoolLabelsContext';
import { useBatches, batchLabel } from '../hooks/useBatches';

const POOL_COLORS: Record<string, string> = {
  SALES: 'gold',
  MEDICAL_OPS: 'green',
  BACKEND: 'blue',
  EXECUTIVE: 'red',
  MIDDLE_MGMT: 'volcano',
  FINANCE_DATA: 'cyan',
  MARKETING_ACQUISITION: 'purple',
};

interface Anchor {
  range: string;
  level: string;
  desc: string;
}
interface Dimension {
  code: string;
  name: string;
  desc: string;
  anchors: Anchor[];
}

interface CompetencyItemRow {
  id: number;
  name: string;
  definition: string;
  levels: Anchor[];
  enabled: boolean;
}

const DEFAULT_ANCHORS: Anchor[] = [
  { range: '0-1', level: '待发展', desc: '' },
  { range: '2-3', level: '胜任', desc: '' },
  { range: '4-5', level: '优秀', desc: '' },
  { range: '6-7', level: '卓越', desc: '' },
];

function cloneDimensions(dims: Dimension[]): Dimension[] {
  return dims.map((d) => ({
    ...d,
    anchors: d.anchors.map((a) => ({ ...a })),
  }));
}

function newBlankDimension(suffix: number): Dimension {
  return {
    code: `CUSTOM_${Date.now()}_${suffix}`,
    name: '',
    desc: '',
    anchors: DEFAULT_ANCHORS.map((a) => ({ ...a })),
  };
}

function competencyItemToDimension(item: CompetencyItemRow): Dimension {
  const anchors: Anchor[] = DEFAULT_ANCHORS.map((def, i) => {
    const hit = item.levels?.find((l) => l.range === def.range) ?? item.levels?.[i];
    return {
      range: def.range,
      level: hit?.level ?? def.level,
      desc: hit?.desc ?? '',
    };
  });
  return {
    code: item.name.replace(/\s+/g, '_').toUpperCase().slice(0, 32) || `ITEM_${item.id}`,
    name: item.name,
    desc: item.definition || '',
    anchors,
  };
}

const ANCHOR_LEVEL_COLOR: Record<string, string> = {
  卓越: 'red',
  优秀: 'orange',
  胜任: 'blue',
  待发展: 'default',
};

function DimensionRubricPopover({
  dim,
  stopParentClick,
}: {
  dim: Dimension;
  stopParentClick?: boolean;
}) {
  const content = (
    <div style={{ maxWidth: 420, maxHeight: 'min(60vh, 480px)', overflow: 'auto' }}>
      <Typography.Paragraph style={{ marginBottom: 12, color: 'rgba(0,0,0,0.75)', whiteSpace: 'pre-wrap' }}>
        {dim.desc || '（未配置维度说明）'}
      </Typography.Paragraph>
      <Divider plain style={{ margin: '8px 0 10px' }}>
        各分数区间行为标准
      </Divider>
      {dim.anchors.map((a) => (
        <div key={a.range} style={{ marginBottom: 10 }}>
          <Tag color={ANCHOR_LEVEL_COLOR[a.level] ?? 'processing'} style={{ marginBottom: 4 }}>
            {a.range} · {a.level}
          </Tag>
          <div style={{ color: '#555', fontSize: 13, lineHeight: 1.55 }}>{a.desc}</div>
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      trigger={['click']}
      placement="bottomLeft"
      title={
        <span>
          {dim.name}
          <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12, fontWeight: 'normal' }}>
            {dim.code}
          </Typography.Text>
        </span>
      }
      content={content}
    >
      <button
        type="button"
        aria-label={`${dim.name}：说明与评分标准`}
        title="说明与评分标准"
        onClick={stopParentClick ? (e) => e.stopPropagation() : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          marginLeft: 2,
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#722ed1',
          fontSize: 11,
          lineHeight: 1,
          verticalAlign: 'middle',
          borderRadius: '50%',
        }}
      >
        ●
      </button>
    </Popover>
  );
}

interface Template {
  id: number;
  batchId: number;
  pool: string;
  positionCategory: string;
  dimensionCount: number;
  fullScore: number;
  dimensions: Dimension[];
  relationWeights: Record<string, number>;
}

function DimensionEditor({
  dimensions,
  onChange,
  onRemove,
}: {
  dimensions: Dimension[];
  onChange: (next: Dimension[]) => void;
  onRemove: (index: number) => void;
}) {
  const patchDim = (index: number, patch: Partial<Dimension>) => {
    onChange(dimensions.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };
  const patchAnchor = (dimIndex: number, anchorIndex: number, desc: string) => {
    onChange(
      dimensions.map((d, i) => {
        if (i !== dimIndex) return d;
        const anchors = d.anchors.map((a, ai) => (ai === anchorIndex ? { ...a, desc } : a));
        return { ...d, anchors };
      }),
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {dimensions.map((d, idx) => (
        <Card
          key={`${d.code}-${idx}`}
          size="small"
          title={`维度 ${idx + 1}`}
          extra={
            dimensions.length > 1 ? (
              <Popconfirm title="确认移除此维度？" onConfirm={() => onRemove(idx)}>
                <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                  移除
                </Button>
              </Popconfirm>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                至少保留 1 项
              </Typography.Text>
            )
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Input
              value={d.name}
              placeholder="素质项名称（必填）"
              onChange={(e) => patchDim(idx, { name: e.target.value })}
            />
            <Input.TextArea
              rows={2}
              value={d.desc}
              placeholder="素质项定义 / 维度说明"
              onChange={(e) => patchDim(idx, { desc: e.target.value })}
            />
            {d.anchors.map((a, ai) => (
              <div key={a.range}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {a.level}（{a.range} 分）
                </Typography.Text>
                <Input.TextArea
                  rows={2}
                  value={a.desc}
                  placeholder={`${a.level}档行为描述`}
                  onChange={(e) => patchAnchor(idx, ai, e.target.value)}
                />
              </div>
            ))}
          </Space>
        </Card>
      ))}
    </div>
  );
}

export function TemplatesQualityPage() {
  const { getEvalRoleLabel, enabledCodes } = useEvalRoleLabels();
  const { getTalentPoolLabel, evaluateeDisplayOrder } = useTalentPoolLabels();
  const { batches, batchId, setBatchId } = useBatches();
  const { data: list = [], isLoading } = useQuery<Template[]>({
    queryKey: ['quality-templates', batchId],
    queryFn: () => api.get('/admin/quality-templates', { params: { batchId } }),
    enabled: !!batchId,
  });

  const [activePool, setActivePool] = useState<string | null>(null);

  /** 每池只展示一条（同批次若历史脏数据有多条，取 id 最大 = 最新） */
  const templateByPool = useMemo(() => {
    const byPool = new Map<string, Template>();
    for (const t of list) {
      const prev = byPool.get(t.pool);
      if (!prev || t.id > prev.id) byPool.set(t.pool, t);
    }
    return byPool;
  }, [list]);

  /** 字典里已启用且作为被评价人的人才池；无模板者显示「待初始化」卡片 */
  const poolCards = useMemo(
    () =>
      evaluateeDisplayOrder.map((pool) => ({
        pool,
        template: templateByPool.get(pool) ?? null,
      })),
    [evaluateeDisplayOrder, templateByPool],
  );

  const missingPoolCount = useMemo(
    () => poolCards.filter((c) => !c.template).length,
    [poolCards],
  );

  const sorted = useMemo(
    () =>
      poolCards
        .filter((c) => c.template)
        .map((c) => c.template!),
    [poolCards],
  );

  const active = useMemo(() => sorted.find((t) => t.pool === activePool), [sorted, activePool]);

  const qc = useQueryClient();
  const [editingWeights, setEditingWeights] = useState(false);
  const [draftWeights, setDraftWeights] = useState<Record<string, number>>({});
  const [editingDimensions, setEditingDimensions] = useState(false);
  const [draftDimensions, setDraftDimensions] = useState<Dimension[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryPick, setLibraryPick] = useState<number[]>([]);
  const [libraryQ, setLibraryQ] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyForm] = Form.useForm();

  const copyMutation = useMutation({
    mutationFn: (vars: { fromBatchId: number; toBatchId: number }) =>
      api.post('/admin/quality-templates/copy-from', vars) as Promise<{
        copied: number;
        created: number;
        updated: number;
      }>,
    onSuccess: (r: { copied: number; created: number; updated: number }) => {
      Modal.success({
        title: '复制完成',
        content: `共复制 ${r.copied} 个人才池模板（新建 ${r.created} / 覆盖 ${r.updated}）`,
      });
      setCopyOpen(false);
      copyForm.resetFields();
      qc.invalidateQueries({ queryKey: ['quality-templates'] });
    },
    onError: (e: Error) => message.error(e.message || '复制失败'),
  });

  const ensureMissingMutation = useMutation({
    mutationFn: (vars: { batchId: number; pool?: string }) =>
      api.post('/admin/quality-templates/ensure-missing', vars) as Promise<{
        missing: number;
        created: number;
        updated: number;
        pools: string[];
      }>,
    onSuccess: (r) => {
      if (r.created === 0 && r.missing === 0) {
        message.info('所有已启用人才池均已有维度模板');
      } else {
        message.success(`已初始化 ${r.created} 个人才池模板`);
      }
      qc.invalidateQueries({ queryKey: ['quality-templates'] });
    },
    onError: (e: Error) => message.error(e.message || '初始化失败'),
  });

  const { data: libraryItems = [] } = useQuery({
    queryKey: ['competency-items', 'enabled'],
    queryFn: () => api.get<unknown, CompetencyItemRow[]>('/admin/competency-items?enabled=true'),
    enabled: libraryOpen,
  });

  const saveWeights = useMutation({
    mutationFn: (vars: { id: number; relationWeights: Record<string, number> }) =>
      api.patch(`/admin/quality-templates/${vars.id}`, { relationWeights: vars.relationWeights }),
    onSuccess: () => {
      message.success('评价人权重已更新');
      setEditingWeights(false);
      qc.invalidateQueries({ queryKey: ['quality-templates'] });
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  });

  const saveDimensions = useMutation({
    mutationFn: (vars: { id: number; dimensions: Dimension[] }) =>
      api.patch(`/admin/quality-templates/${vars.id}`, { dimensions: vars.dimensions }),
    onSuccess: () => {
      message.success('评价维度已保存');
      setEditingDimensions(false);
      qc.invalidateQueries({ queryKey: ['quality-templates'] });
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  });

  const draftWeightsSum = useMemo(
    () => Object.values(draftWeights).reduce((acc, v) => acc + (Number(v) || 0), 0),
    [draftWeights],
  );
  // 维度库口径（PR-3）：评价人权重合计必须恰好 = 100%（容差 ±0.01），0% 角色可保留。
  const weightsSumValid = Math.abs(draftWeightsSum - 100) <= 0.01;

  const startEditWeights = () => {
    if (active) {
      setDraftWeights({ ...active.relationWeights });
      setEditingWeights(true);
      setEditingDimensions(false);
    }
  };

  const startEditDimensions = () => {
    if (active) {
      setDraftDimensions(cloneDimensions(active.dimensions));
      setEditingDimensions(true);
      setEditingWeights(false);
    }
  };

  const addFromLibrary = () => {
    const existing = new Set(draftDimensions.map((d) => d.name.trim()));
    const picked = libraryItems.filter((it) => libraryPick.includes(it.id));
    const toAdd = picked
      .filter((it) => !existing.has(it.name.trim()))
      .map(competencyItemToDimension);
    if (toAdd.length === 0) {
      message.warning('所选素质项已在模板中，或尚未勾选');
      return;
    }
    setDraftDimensions((prev) => [...prev, ...toAdd]);
    setLibraryOpen(false);
    setLibraryPick([]);
    message.success(`已加入 ${toAdd.length} 个维度`);
  };

  const filteredLibrary = useMemo(() => {
    const q = libraryQ.trim().toLowerCase();
    if (!q) return libraryItems;
    return libraryItems.filter(
      (it) => it.name.toLowerCase().includes(q) || (it.definition || '').toLowerCase().includes(q),
    );
  }, [libraryItems, libraryQ]);

  if (isLoading) {
    return (
      <div style={{ ...adminPageWrap, display: 'flex', justifyContent: 'center', padding: '80px 24px 24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">素质项评价</div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8, maxWidth: 920, fontSize: 13 }}>
        按人才池配置本批次要用的素质项、评价人权重与行为锚点。点进人才池卡片可<b>增删改维度</b>，也可从
        <Link to="/data-dictionary?tab=competency-items"> 素质项库 </Link>
        挑选加入；保存后立即生效。
      </Typography.Paragraph>

      <Space style={{ marginBottom: 12 }} wrap>
        <span style={{ color: '#666' }}>当前批次：</span>
        <Select
          style={{ minWidth: 260 }}
          placeholder="选择盘点批次"
          value={batchId}
          onChange={setBatchId}
          options={batches.map((b) => ({ value: b.id, label: batchLabel(b) }))}
        />
        <Button
          icon={<CopyOutlined />}
          onClick={() => {
            copyForm.resetFields();
            if (batchId) copyForm.setFieldValue('toBatchId', batchId);
            setCopyOpen(true);
          }}
        >
          从上一批次复制模板
        </Button>
        {missingPoolCount > 0 && batchId && (
          <Button
            type="primary"
            loading={ensureMissingMutation.isPending}
            onClick={() => ensureMissingMutation.mutate({ batchId })}
          >
            补全缺失模板（{missingPoolCount} 个人才池）
          </Button>
        )}
      </Space>

      {missingPoolCount > 0 && batchId && (
        <Alert
          type="warning"
          showIcon
          style={{ ...adminAlertStyle, marginBottom: 12 }}
          message={`有 ${missingPoolCount} 个已启用人才池尚未配置维度模板`}
          description="新建自定义人才池后，需为当前批次初始化模板（默认沿用综合职能池的 5 维配置与评价人权重）。可点上方「补全缺失模板」一键初始化，或在下方卡片逐池初始化。"
        />
      )}

      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message="规则总览（2026 上半年）"
        description={
          <div>
            <div>· <b>通关式打分</b>：每维 0-7 分（待发展 0-1 / 胜任 2-3 / 优秀 4-5 / 卓越 6-7），不允许越级。</div>
            <div>· <b>满分</b> = 7 × 维度数；按各类评价人「类内平均 → 加权汇总」算综合素质标准分（百分制）。</div>
            <div>· <b>缺类</b>时按比例放大其余权重，使其加和回到 100%。</div>
            <div>· <b>九宫格</b>：在同一人才池内按综合素质标准分排序，前 30% 高 / 中 50% / 后 20% 低。</div>
          </div>
        }
      />

      <Row gutter={[16, 16]} style={{ alignItems: 'stretch', marginTop: 16 }}>
        {poolCards.map(({ pool, template: t }) =>
          t ? (
          <Col key={t.id} xs={24} sm={12} md={8} xl={8} style={{ display: 'flex' }}>
            <Card
              hoverable
              size="small"
              onClick={() => setActivePool(t.pool)}
              style={{
                ...adminContentCardStyle,
                flex: 1,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: activePool === t.pool ? '2px solid #722ed1' : '1px solid rgba(0, 0, 0, 0.06)',
                boxShadow:
                  activePool === t.pool
                    ? '0 8px 28px rgba(114, 46, 209, 0.18)'
                    : adminContentCardStyle.boxShadow,
              }}
              styles={{ header: { borderBottom: '1px solid rgba(0,0,0,0.04)' } }}
              title={
                <div>
                  <Tag color={POOL_COLORS[t.pool] || 'default'}>{getTalentPoolLabel(t.pool)}</Tag>
                  <span style={{ color: '#888', fontSize: 12 }}>模板 #{t.id}</span>
                </div>
              }
            >
              <Row gutter={8}>
                <Col span={12}><Statistic title="维度数" value={t.dimensionCount} /></Col>
                <Col span={12}><Statistic title="满分" value={t.fullScore} /></Col>
              </Row>
              <div style={{ marginTop: 8 }}>
                <span style={{ color: '#888' }}>权重：</span>
                {Object.entries(t.relationWeights).map(([k, v]) => (
                  <Tag key={k} color="processing" style={{ marginBottom: 4 }}>
                    {getEvalRoleLabel(t.pool, k)} {v}%
                  </Tag>
                ))}
              </div>
              <div
                style={{
                  marginTop: 8,
                  color: '#666',
                  fontSize: 12,
                  lineHeight: 1.8,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '0 4px',
                }}
              >
                {t.dimensions.map((d, idx) => (
                  <span key={d.code} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {idx > 0 ? <span style={{ color: '#bbb' }}> · </span> : null}
                    <span>{d.name}</span>
                    <DimensionRubricPopover dim={d} stopParentClick />
                  </span>
                ))}
              </div>
            </Card>
          </Col>
          ) : (
          <Col key={`missing-${pool}`} xs={24} sm={12} md={8} xl={8} style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                ...adminContentCardStyle,
                flex: 1,
                border: '1px dashed #faad14',
                background: '#fffbe6',
              }}
              styles={{ header: { borderBottom: '1px solid rgba(0,0,0,0.04)' } }}
              title={
                <div>
                  <Tag color="warning">{getTalentPoolLabel(pool)}</Tag>
                  <span style={{ color: '#ad6800', fontSize: 12 }}>待初始化</span>
                </div>
              }
            >
              <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
                该人才池已在字典中启用，但当前批次尚无维度模板。
              </Typography.Paragraph>
              <Button
                type="primary"
                block
                loading={ensureMissingMutation.isPending}
                disabled={!batchId}
                onClick={() => batchId && ensureMissingMutation.mutate({ batchId, pool })}
              >
                初始化默认模板
              </Button>
            </Card>
          </Col>
          ),
        )}
      </Row>

      {poolCards.length === 0 && batchId && (
        <Empty
          description={
            <span>
              当前批次还没有素质项评价模板。新建批次时系统会自动初始化；也可点上方「从上一批次复制模板」。
            </span>
          }
        />
      )}

      {!batchId && !isLoading && (
        <Empty description="请先在上方选择盘点批次" />
      )}

      <Modal
        title={
          active ? (
            <div>
              <Tag color={POOL_COLORS[active.pool] || 'default'}>{getTalentPoolLabel(active.pool)}</Tag>
              <span style={{ marginLeft: 8 }}>评价维度配置</span>
              <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>模板 #{active.id}</span>
            </div>
          ) : null
        }
        open={!!active}
        onCancel={() => {
          setActivePool(null);
          setEditingWeights(false);
          setEditingDimensions(false);
        }}
        footer={null}
        width={920}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
      >
        {active && (
          <>
            <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="维度数">
                {editingDimensions ? draftDimensions.length : active.dimensionCount}
              </Descriptions.Item>
              <Descriptions.Item label="满分">
                {editingDimensions ? 7 * draftDimensions.length : active.fullScore}
              </Descriptions.Item>
              <Descriptions.Item label="权重模型" span={2}>
                {editingWeights ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                      {Object.keys(draftWeights).map((k) => (
                        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {getEvalRoleLabel(active.pool, k)}：
                          <InputNumber
                            min={0}
                            value={draftWeights[k]}
                            onChange={(v) => setDraftWeights((p) => ({ ...p, [k]: Number(v ?? 0) }))}
                            style={{ width: 80 }}
                          />
                          <Popconfirm
                            title={`移除「${getEvalRoleLabel(active.pool, k)}」？`}
                            description="保存后本池不再使用该角色（评价关系列/组织同步都不会再带它）；已派任务不会自动删除。"
                            okText="移除"
                            cancelText="取消"
                            onConfirm={() =>
                              setDraftWeights((prev) => {
                                const next = { ...prev };
                                delete next[k];
                                return next;
                              })
                            }
                          >
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              aria-label={`移除${getEvalRoleLabel(active.pool, k)}`}
                            />
                          </Popconfirm>
                        </span>
                      ))}
                      {enabledCodes
                        .filter((c) => !(c in draftWeights))
                        .length > 0 && (
                        <Select
                          placeholder="从字典添加角色"
                          style={{ width: 160 }}
                          options={enabledCodes
                            .filter((c) => !(c in draftWeights))
                            .map((c) => ({
                              value: c,
                              label: `${getEvalRoleLabel(active.pool, c)}（${c}）`,
                            }))}
                          onChange={(code) => {
                            if (!code) return;
                            setDraftWeights((p) => ({ ...p, [code]: 0 }));
                          }}
                          value={null as unknown as string}
                        />
                      )}
                    </Space>
                    <Space align="center" wrap>
                      <Tag color={weightsSumValid ? 'success' : 'error'}>
                        合计 {Number(draftWeightsSum.toFixed(2))}%
                      </Tag>
                      {!weightsSumValid && (
                        <Typography.Text type="danger" style={{ fontSize: 12 }}>
                          评价人权重合计必须恰好等于 100% 才能保存；不需要的角色可点右侧删除图标移除
                        </Typography.Text>
                      )}
                    </Space>
                    <Space>
                      <Button
                        size="small"
                        type="primary"
                        disabled={!weightsSumValid}
                        loading={saveWeights.isPending}
                        onClick={() => saveWeights.mutate({ id: active.id, relationWeights: draftWeights })}
                      >
                        保存权重
                      </Button>
                      <Button size="small" onClick={() => setEditingWeights(false)}>
                        取消
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space wrap align="center">
                    {Object.entries(active.relationWeights).map(([k, v]) => (
                      <Tag key={k} color="processing">{getEvalRoleLabel(active.pool, k)} {v}%</Tag>
                    ))}
                    {!editingDimensions && (
                      <Button size="small" type="link" onClick={startEditWeights}>
                        编辑权重
                      </Button>
                    )}
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>

            {editingDimensions ? (
              <>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Button
                    icon={<BookOutlined />}
                    onClick={() => {
                      setLibraryPick([]);
                      setLibraryQ('');
                      setLibraryOpen(true);
                    }}
                  >
                    从素质项库添加
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setDraftDimensions((prev) => [...prev, newBlankDimension(prev.length)])}
                  >
                    新增空白维度
                  </Button>
                  <Button
                    type="primary"
                    loading={saveDimensions.isPending}
                    onClick={() => {
                      if (draftDimensions.some((d) => !d.name.trim())) {
                        message.warning('请填写每个维度的名称');
                        return;
                      }
                      saveDimensions.mutate({ id: active.id, dimensions: draftDimensions });
                    }}
                  >
                    保存维度
                  </Button>
                  <Button onClick={() => setEditingDimensions(false)}>取消</Button>
                </Space>
                <DimensionEditor
                  dimensions={draftDimensions}
                  onChange={setDraftDimensions}
                  onRemove={(idx) => setDraftDimensions((prev) => prev.filter((_, i) => i !== idx))}
                />
              </>
            ) : (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Button type="primary" onClick={startEditDimensions}>
                    编辑维度（增删改）
                  </Button>
                </Space>
                <Collapse
                  size="small"
                  items={active.dimensions.map((d, i) => ({
                    key: d.code,
                    label: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <b>{i + 1}. {d.name}</b>
                        <span style={{ color: '#888', fontSize: 12 }}>{d.code}</span>
                        <DimensionRubricPopover dim={d} stopParentClick />
                      </span>
                    ),
                    children: (
                      <div>
                        <p style={{ color: '#444' }}>{d.desc}</p>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                          各区间行为锚点：
                        </Typography.Text>
                        {d.anchors.map((a) => (
                          <div key={a.range} style={{ marginBottom: 6 }}>
                            <Tag color={ANCHOR_LEVEL_COLOR[a.level] ?? 'default'}>
                              {a.range} · {a.level}
                            </Tag>
                            <span style={{ color: '#555' }}>{a.desc}</span>
                          </div>
                        ))}
                      </div>
                    ),
                  }))}
                />
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        title="从素质项库添加"
        open={libraryOpen}
        onCancel={() => setLibraryOpen(false)}
        onOk={addFromLibrary}
        okText="加入所选"
        width={720}
      >
        <Input.Search
          placeholder="搜索素质项名称或定义"
          allowClear
          style={{ marginBottom: 12 }}
          value={libraryQ}
          onChange={(e) => setLibraryQ(e.target.value)}
        />
        <Table
          rowKey="id"
          size="small"
          pagination={{ pageSize: 8 }}
          dataSource={filteredLibrary}
          rowSelection={{
            selectedRowKeys: libraryPick,
            onChange: (keys) => setLibraryPick(keys as number[]),
            getCheckboxProps: (r) => ({
              disabled: draftDimensions.some((d) => d.name.trim() === r.name.trim()),
            }),
          }}
          columns={[
            { title: '素质项', dataIndex: 'name', width: 140 },
            { title: '定义', dataIndex: 'definition', ellipsis: true },
          ]}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          已在当前模板中的项不可重复勾选。更多条目请先在
          <Link to="/data-dictionary?tab=competency-items"> 素质项库 </Link>
          导入或新增。
        </Typography.Text>
      </Modal>

      <Modal
        title="从上一批次复制素质维度模板"
        open={copyOpen}
        onCancel={() => setCopyOpen(false)}
        onOk={async () => {
          try {
            const vals = await copyForm.validateFields();
            if (vals.fromBatchId === vals.toBatchId) {
              message.warning('源批次与目标批次不能相同');
              return;
            }
            copyMutation.mutate({ fromBatchId: vals.fromBatchId, toBatchId: vals.toBatchId });
          } catch {
            /* form 自校验已提示 */
          }
        }}
        confirmLoading={copyMutation.isPending}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="会把源批次 6 个人才池的维度 + 评价人权重 + 角色配置复制到目标批次；目标批次已存在的同名人才池将被覆盖。"
        />
        <Form form={copyForm} layout="vertical">
          <Form.Item
            name="fromBatchId"
            label="复制自（源批次）"
            rules={[{ required: true, message: '请选择源批次' }]}
          >
            <Select
              placeholder="选择源批次"
              options={batches.map((b) => ({ label: batchLabel(b), value: b.id }))}
            />
          </Form.Item>
          <Form.Item
            name="toBatchId"
            label="复制到（目标批次）"
            rules={[{ required: true, message: '请选择目标批次' }]}
          >
            <Select
              placeholder="选择目标批次"
              options={batches.map((b) => ({ label: batchLabel(b), value: b.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
